import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, MessageCircle, Heart, Zap, User, Bot } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface AIDemoProps {
  isActive?: boolean;
  onClose?: () => void;
}

const demoConversation = [
  {
    type: 'user',
    message: "I've been having trouble communicating with my partner lately. We seem to argue about small things.",
    timestamp: '2:34 PM'
  },
  {
    type: 'ai',
    message: "I understand this can be frustrating. Based on your recent mood logs, I notice stress levels have been higher in the evenings. Let's explore some communication strategies that might help.",
    timestamp: '2:34 PM',
    insights: ['Stress correlation detected', 'Evening pattern identified']
  },
  {
    type: 'user',
    message: "Yes, we do tend to argue more after work. How did you know that?",
    timestamp: '2:35 PM'
  },
  {
    type: 'ai',
    message: "Your wearable data shows elevated heart rate variability around 6-8 PM, which often correlates with relationship stress. I suggest trying a 10-minute decompression routine before discussing daily topics.",
    timestamp: '2:35 PM',
    insights: ['HRV pattern analysis', 'Personalized recommendation']
  },
  {
    type: 'user',
    message: "That makes sense. What kind of decompression routine?",
    timestamp: '2:36 PM'
  },
  {
    type: 'ai',
    message: "Based on your preferences, I recommend: 1) 5 minutes of deep breathing, 2) Share one positive moment from your day, 3) Set an intention for your evening together. Would you like me to set up a daily reminder?",
    timestamp: '2:36 PM',
    insights: ['Personalized strategy', 'Behavioral intervention']
  }
];

export const AIDemo: React.FC<AIDemoProps> = ({ isActive = false, onClose }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!isActive) {
      setCurrentMessageIndex(0);
      setIsTyping(false);
      return;
    }

    const timer = setInterval(() => {
      if (currentMessageIndex < demoConversation.length - 1) {
        setIsTyping(true);
        setTimeout(() => {
          setCurrentMessageIndex(prev => prev + 1);
          setIsTyping(false);
        }, 1500);
      }
    }, 3000);

    return () => clearInterval(timer);
  }, [isActive, currentMessageIndex]);

  if (!isActive) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl rounded-3xl border border-white/20 dark:border-gray-700/20 max-w-2xl w-full max-h-[80vh] overflow-hidden"
        initial={prefersReducedMotion ? {} : { scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 dark:border-gray-700/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  AI Relationship Coach
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Powered by Gemini 2.5 Flash
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 dark:bg-gray-800/10 flex items-center justify-center hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200"
            >
              ×
            </button>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="p-6 max-h-96 overflow-y-auto">
          <div className="space-y-4">
            <AnimatePresence>
              {demoConversation.slice(0, currentMessageIndex + 1).map((message, index) => (
                <motion.div
                  key={index}
                  className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {message.type === 'ai' && (
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                  )}
                  
                  <div className={`max-w-xs lg:max-w-md ${message.type === 'user' ? 'order-1' : ''}`}>
                    <div className={`p-4 rounded-2xl ${
                      message.type === 'user'
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white'
                        : 'bg-white/10 dark:bg-gray-800/10 text-gray-900 dark:text-white'
                    }`}>
                      <p className="text-sm leading-relaxed">{message.message}</p>
                    </div>
                    
                    {message.insights && (
                      <motion.div
                        className="mt-2 space-y-1"
                        initial={prefersReducedMotion ? {} : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                      >
                        {message.insights.map((insight, insightIndex) => (
                          <div
                            key={insightIndex}
                            className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400"
                          >
                            <Zap className="w-3 h-3 text-yellow-500" />
                            {insight}
                          </div>
                        ))}
                      </motion.div>
                    )}
                    
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {message.timestamp}
                    </p>
                  </div>
                  
                  {message.type === 'user' && (
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
            
            {/* Typing Indicator */}
            <AnimatePresence>
              {isTyping && (
                <motion.div
                  className="flex gap-3 justify-start"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-white/10 dark:bg-gray-800/10 p-4 rounded-2xl">
                    <div className="flex space-x-1">
                      <motion.div
                        className="w-2 h-2 bg-gray-500 rounded-full"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                      />
                      <motion.div
                        className="w-2 h-2 bg-gray-500 rounded-full"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                      />
                      <motion.div
                        className="w-2 h-2 bg-gray-500 rounded-full"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 dark:border-gray-700/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Heart className="w-4 h-4 text-red-500" />
              AI analyzing relationship patterns
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-medium hover:shadow-lg transition-all duration-200"
            >
              Try Premium
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};